import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  HandHeart, Award, Scale, Heart, Crown, Sparkles, ScrollText,
  Printer, Copy, DollarSign, Plus, FileText, CheckCircle2,
} from "lucide-react";

const TIER_COLORS = {
  Applicant: "bg-muted text-muted-foreground",
  Bronze:    "bg-[hsl(28_60%_40%)] text-white",
  Silver:    "bg-[hsl(220_10%_65%)] text-black",
  Gold:      "bg-gold text-secondary-foreground",
  Platinum:  "bg-[hsl(275_60%_92%)] text-[hsl(262_55%_15%)]",
};

function StatCard({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="hoa-card p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="w-10 h-10 rounded-lg bg-primary/25 border border-gold flex items-center justify-center">
          <Icon className="w-5 h-5 text-gold" strokeWidth={2.2} />
        </div>
      </div>
      <div className="text-3xl font-semibold text-gold font-mono">{value}</div>
      <div className="text-sm text-muted-foreground mt-1">{label}</div>
      {sub && <div className="text-xs text-muted-foreground/80 mt-1">{sub}</div>}
    </div>
  );
}

function fmt(n) { return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

export default function Advocacy() {
  const { user } = useAuth();
  const isBoard = user?.role === "admin" || user?.role === "board";
  const [partnership, setPartnership] = useState(null);
  const [donations, setDonations] = useState([]);
  const [impact, setImpact] = useState(null);
  const [resolution, setResolution] = useState(null);
  const [certificate, setCertificate] = useState(null);

  const [year, setYear] = useState(new Date().getFullYear());
  const [regOpen, setRegOpen] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);
  const [receiptDoc, setReceiptDoc] = useState(null);

  const [pForm, setPForm] = useState({
    hoa_name: "", hoa_address: "", primary_contact: "",
    community_size: 0, mission_alignment_statement: "", monthly_subscription: 79,
  });
  const [dForm, setDForm] = useState({ donor_name: user?.name || "", donor_email: user?.email || "", amount: "", note: "" });

  const load = useCallback(async () => {
    try {
      const [p, d, c] = await Promise.all([
        api.get("/advocacy/partnership"),
        api.get("/advocacy/donations"),
        api.get("/advocacy/certificate"),
      ]);
      setPartnership(p.data);
      setDonations(d.data);
      setCertificate(c.data);
      setPForm({
        hoa_name: p.data.hoa_name || "",
        hoa_address: p.data.hoa_address || "",
        primary_contact: p.data.primary_contact || "",
        community_size: p.data.community_size || 0,
        mission_alignment_statement: p.data.mission_alignment_statement || "",
        monthly_subscription: p.data.monthly_subscription || 79,
      });
    } catch (err) {
      console.error("Advocacy load failed:", err);
      toast.error("Could not load advocacy portal");
    }
  }, []);

  const loadImpact = useCallback(async (y) => {
    const { data } = await api.get(`/advocacy/impact/${y}`);
    setImpact(data);
  }, []);

  const loadResolution = async () => {
    if (!isBoard) return;
    const { data } = await api.get("/advocacy/resolution");
    setResolution(data);
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadImpact(year); }, [loadImpact, year, partnership?.registered]);

  const submitPartnership = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/advocacy/partnership", { ...pForm, community_size: Number(pForm.community_size), monthly_subscription: Number(pForm.monthly_subscription) });
      setPartnership(data);
      toast.success("Advocacy Partnership saved");
      setRegOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
  };

  const submitDonation = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/advocacy/donations", { ...dForm, amount: Number(dForm.amount) });
      toast.success(`Donation recorded · Receipt ${data.receipt_number}`);
      setDForm({ donor_name: user?.name || "", donor_email: user?.email || "", amount: "", note: "" });
      setDonateOpen(false);
      load();
      loadImpact(year);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record donation");
    }
  };

  const openReceipt = async (id) => {
    const { data } = await api.get(`/advocacy/donations/${id}/receipt`);
    setReceiptDoc(data);
  };

  const copyResolution = async () => {
    if (!resolution) await loadResolution();
    setTimeout(async () => {
      const { data } = await api.get("/advocacy/resolution");
      await navigator.clipboard.writeText(data.resolution);
      toast.success("Resolution copied to clipboard");
    }, 0);
  };

  const printResolution = async () => {
    if (!resolution) {
      const { data } = await api.get("/advocacy/resolution");
      setResolution(data);
      setTimeout(() => window.print(), 200);
    } else {
      window.print();
    }
  };

  if (!partnership) {
    return <div className="text-muted-foreground">Loading advocacy portal…</div>;
  }

  const registered = partnership.registered;

  return (
    <div className="space-y-8" data-testid="advocacy-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-gold text-sm font-medium mb-2">
            <HandHeart className="w-4 h-4" /> Community Advocacy Partner
          </div>
          <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight" data-testid="advocacy-title">
            {registered ? partnership.hoa_name : "Become an Advocacy Partner"}
          </h1>
          <p className="no-gold text-foreground/75 mt-2 max-w-2xl">
            {registered
              ? `Partner since ${new Date(partnership.partner_since).toLocaleDateString()} · 80% of every subscription dollar goes to ${partnership.nonprofit_name}.`
              : "Register your HOA to activate the Advocacy Partner program — badge, certificate, impact reports, and 501(c)(3) donations."}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${TIER_COLORS[partnership.badge_tier]}`} data-testid="advocacy-badge-tier">
            <Crown className="w-3.5 h-3.5 inline mr-1" /> {partnership.badge_tier} Partner
          </span>
        </div>
      </div>

      {/* Not registered CTA */}
      {!registered && isBoard && (
        <div className="hoa-card royal-ring p-8 text-center relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-primary/25 blur-3xl" />
          <div className="relative">
            <Sparkles className="w-8 h-8 text-gold mx-auto mb-3" />
            <h2 className="text-2xl font-semibold mb-2">Activate your Advocacy Partnership</h2>
            <p className="no-gold text-foreground/75 max-w-xl mx-auto mb-6">
              One-time registration. No fees. You'll receive a partner certificate immediately and
              be able to generate a board resolution, accept 501(c)(3) donations, and publish annual impact.
            </p>
            <Button className="rounded-full h-11 bg-gold text-secondary-foreground hover:brightness-110 glow-gold" onClick={() => setRegOpen(true)} data-testid="register-partnership-btn">
              <Award className="w-4 h-4 mr-2" /> Register partnership
            </Button>
          </div>
        </div>
      )}

      {!registered && !isBoard && (
        <div className="hoa-card p-6 text-center" data-testid="not-registered-owner">
          <p className="no-gold text-foreground/80">Your HOA hasn't activated Advocacy Partnership yet. Ask your board to visit this page to register.</p>
        </div>
      )}

      {/* Registered tabs */}
      {registered && (
        <Tabs defaultValue="overview">
          <TabsList className="bg-card border border-border" data-testid="advocacy-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="donations" data-testid="tab-donations">Donations</TabsTrigger>
            <TabsTrigger value="impact" data-testid="tab-impact">Impact Report</TabsTrigger>
            {isBoard && <TabsTrigger value="resolution" data-testid="tab-resolution">Board Resolution</TabsTrigger>}
            <TabsTrigger value="certificate" data-testid="tab-certificate">Certificate</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="mt-6 space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="advocacy-stats">
              <StatCard label="Total impact" value={fmt(partnership.total_impact)} icon={Heart} testid="stat-total" />
              <StatCard label="From subscription" value={fmt(partnership.subscription_contribution)} sub={`${partnership.months_active} mo × ${partnership.advocacy_percentage}%`} icon={Scale} testid="stat-sub" />
              <StatCard label="From donations" value={fmt(partnership.total_donations)} sub={`${donations.length} contribution${donations.length===1?"":"s"}`} icon={DollarSign} testid="stat-donations" />
              <StatCard label="Tier" value={partnership.badge_tier} icon={Award} testid="stat-tier" />
            </div>

            <div className="hoa-card p-6" data-testid="how-it-works-card">
              <h3 className="text-lg font-semibold mb-3">How it works</h3>
              <ol className="no-gold space-y-2 text-sm text-foreground/80 list-decimal list-inside">
                <li>Your monthly CommunityIQ subscription auto-transfers <span className="text-gold font-medium">{partnership.advocacy_percentage}%</span> to {partnership.nonprofit_name} (501(c)(3), EIN {partnership.nonprofit_ein}).</li>
                <li>Owners, attorneys, and trustees can add optional tax-deductible donations right here — receipts auto-generate.</li>
                <li>Each January we publish an impact report showing exactly where the money went.</li>
                <li>Present it at your annual meeting. Your board resolution language is already written for you.</li>
              </ol>
            </div>

            {isBoard && (
              <div className="text-right">
                <Button variant="outline" onClick={() => setRegOpen(true)} data-testid="edit-partnership-btn">
                  Edit partnership details
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Donations */}
          <TabsContent value="donations" className="mt-6 space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold">Tax-deductible donations</h3>
              <Button className="rounded-full h-11 bg-gold text-secondary-foreground hover:brightness-110" onClick={() => setDonateOpen(true)} data-testid="record-donation-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Record donation
              </Button>
            </div>
            <div className="hoa-card overflow-hidden">
              {donations.length === 0 ? (
                <div className="p-10 text-center text-muted-foreground" data-testid="donations-empty">
                  No donations yet. Every contribution to {partnership.nonprofit_name} is 501(c)(3) tax-deductible.
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {donations.map((d) => (
                    <div key={d.id} className="p-4 flex items-center justify-between gap-4" data-testid={`donation-${d.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground">{d.donor_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {d.donor_email || "—"} · {new Date(d.created_at).toLocaleDateString()} · Receipt {d.receipt_number}
                        </div>
                        {d.note && <div className="text-xs text-foreground/70 mt-1 italic">"{d.note}"</div>}
                      </div>
                      <div className="text-right">
                        <div className="text-gold font-mono font-semibold">{fmt(d.amount)}</div>
                        <button
                          onClick={() => openReceipt(d.id)}
                          className="text-xs text-gold hover:underline mt-1 inline-flex items-center gap-1"
                          data-testid={`view-receipt-${d.id}`}
                        >
                          <FileText className="w-3 h-3" /> View receipt
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Impact */}
          <TabsContent value="impact" className="mt-6 space-y-6">
            <div className="flex items-center gap-3">
              <Label>Year:</Label>
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="h-10 px-3 rounded-lg bg-card border border-border"
                data-testid="impact-year-select"
              >
                {[year + 1, year, year - 1, year - 2].filter((y, i, a) => a.indexOf(y) === i).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            {impact && (
              <>
                <div className="hoa-card royal-ring p-8" data-testid="impact-report-card">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-gold mb-1">Annual Impact Report</div>
                      <h3 className="text-2xl font-semibold">{impact.year} · {impact.hoa_name}</h3>
                    </div>
                    <Crown className="w-6 h-6 text-gold" />
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 mb-6">
                    <div className="royal-bg rounded-xl p-4 border border-border">
                      <div className="text-xs text-muted-foreground uppercase mb-1">Subscription contribution</div>
                      <div className="text-2xl font-mono font-semibold text-gold">{fmt(impact.subscription_contribution)}</div>
                      <div className="text-xs text-muted-foreground mt-1">{impact.months_active_this_year} active month{impact.months_active_this_year===1?"":"s"}</div>
                    </div>
                    <div className="royal-bg rounded-xl p-4 border border-border">
                      <div className="text-xs text-muted-foreground uppercase mb-1">Donations</div>
                      <div className="text-2xl font-mono font-semibold text-gold">{fmt(impact.donation_total)}</div>
                      <div className="text-xs text-muted-foreground mt-1">{impact.donation_count} contribution{impact.donation_count===1?"":"s"}</div>
                    </div>
                    <div className="royal-bg rounded-xl p-4 border border-gold">
                      <div className="text-xs text-gold uppercase mb-1">Total impact</div>
                      <div className="text-2xl font-mono font-semibold text-gold">{fmt(impact.total)}</div>
                      <div className="text-xs text-muted-foreground mt-1">{impact.badge_tier} tier</div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-lg font-semibold mb-3">Where it went</h4>
                    <div className="space-y-3">
                      {impact.allocations.map((a) => (
                        <div key={a.category} data-testid={`alloc-${a.category.toLowerCase().replace(/\s+/g,'-')}`}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="no-gold text-foreground/85">{a.category}</span>
                            <span className="font-mono text-gold font-semibold">{fmt(a.amount)} · {a.percentage}%</span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-primary to-[hsl(var(--gold))]" style={{ width: `${a.percentage}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {impact.top_donors.length > 0 && (
                    <div>
                      <h4 className="text-lg font-semibold mb-3">Top donors ({impact.year})</h4>
                      <div className="grid md:grid-cols-2 gap-2">
                        {impact.top_donors.map((d, i) => (
                          <div key={`donor-${d.name}-${i}`} className="flex items-center justify-between p-2 rounded-lg bg-accent/40" data-testid={`top-donor-${i}`}>
                            <span className="text-sm">{i + 1}. {d.name}</span>
                            <span className="font-mono text-gold text-sm">{fmt(d.amount)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-8 pt-6 border-t border-border text-xs text-muted-foreground italic">
                    {impact.nonprofit_name} · 501(c)(3) · EIN {impact.nonprofit_ein}. Report generated by CommunityIQ.
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* Resolution */}
          {isBoard && (
            <TabsContent value="resolution" className="mt-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold">Board resolution language</h3>
                  <p className="no-gold text-sm text-muted-foreground">Ready to paste into your next board meeting minutes.</p>
                </div>
                <div className="flex gap-2">
                  {!resolution && (
                    <Button variant="outline" onClick={loadResolution} data-testid="generate-resolution-btn">
                      <ScrollText className="w-4 h-4 mr-2" /> Generate
                    </Button>
                  )}
                  {resolution && (
                    <>
                      <Button variant="outline" onClick={copyResolution} data-testid="copy-resolution-btn">
                        <Copy className="w-4 h-4 mr-2" /> Copy
                      </Button>
                      <Button variant="outline" onClick={printResolution} data-testid="print-resolution-btn">
                        <Printer className="w-4 h-4 mr-2" /> Print
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <div className="hoa-card p-8 print:bg-white print:text-black">
                {!resolution ? (
                  <div className="text-center text-muted-foreground py-12">
                    Click <b>Generate</b> to produce the resolution language for {partnership.hoa_name}.
                  </div>
                ) : (
                  <pre className="no-gold whitespace-pre-wrap font-serif text-[15px] leading-relaxed text-foreground/90" data-testid="resolution-text">
                    {resolution.resolution}
                  </pre>
                )}
              </div>
            </TabsContent>
          )}

          {/* Certificate */}
          <TabsContent value="certificate" className="mt-6">
            {certificate && (
              <div
                className="hoa-card royal-ring p-12 text-center relative overflow-hidden print:bg-white print:text-black"
                data-testid="certificate-card"
              >
                <div className="absolute inset-0 paper-texture opacity-30" />
                <div className="absolute -top-20 -left-20 w-72 h-72 rounded-full bg-primary/25 blur-3xl" />
                <div className="absolute -bottom-20 -right-20 w-72 h-72 rounded-full bg-[hsl(var(--gold)/0.15)] blur-3xl" />
                <div className="relative">
                  <div className="inline-flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-6">
                    <div className="w-8 h-px bg-gold" />
                    Certificate of Partnership
                    <div className="w-8 h-px bg-gold" />
                  </div>
                  <Crown className="w-14 h-14 text-gold mx-auto mb-4" />
                  <div className="no-gold text-sm text-foreground/70 mb-2">This certifies that</div>
                  <div className="text-4xl md:text-5xl font-serif font-semibold text-gold mb-2 tracking-tight" data-testid="cert-hoa-name">
                    {certificate.hoa_name}
                  </div>
                  <div className="no-gold text-sm text-foreground/70 mb-8 max-w-xl mx-auto leading-relaxed">
                    is a recognized <span className="text-gold font-medium">Community Advocacy Partner</span> of{" "}
                    <span className="text-gold font-medium">{certificate.nonprofit_name}</span>, contributing
                    to the protection and empowerment of homeowners nationwide.
                  </div>
                  <div className="flex items-center justify-center gap-6 mb-8 flex-wrap">
                    <div>
                      <div className="text-xs text-muted-foreground uppercase">Partner tier</div>
                      <div className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-semibold ${TIER_COLORS[certificate.badge_tier]}`}>
                        {certificate.badge_tier}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground uppercase">Total impact</div>
                      <div className="text-xl font-mono font-semibold text-gold mt-1">{fmt(certificate.total_impact)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground uppercase">Partner since</div>
                      <div className="text-sm text-foreground/85 mt-1">{certificate.partner_since ? new Date(certificate.partner_since).toLocaleDateString() : "—"}</div>
                    </div>
                  </div>
                  <div className="no-gold text-xs text-muted-foreground italic">
                    Certificate ID: {certificate.certificate_id} · Issued {new Date(certificate.issued_on).toLocaleDateString()}
                  </div>
                  <div className="mt-6 print:hidden">
                    <Button variant="outline" onClick={() => window.print()} data-testid="print-cert-btn">
                      <Printer className="w-4 h-4 mr-2" /> Print certificate
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Registration dialog */}
      <Dialog open={regOpen} onOpenChange={setRegOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{registered ? "Edit partnership details" : "Register your Advocacy Partnership"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitPartnership} className="space-y-4" data-testid="partnership-form">
            <div>
              <Label>HOA / Community name</Label>
              <Input required value={pForm.hoa_name} onChange={(e) => setPForm({ ...pForm, hoa_name: e.target.value })} className="mt-1.5 h-11" data-testid="p-hoa-name" />
            </div>
            <div>
              <Label>Address</Label>
              <Input value={pForm.hoa_address} onChange={(e) => setPForm({ ...pForm, hoa_address: e.target.value })} className="mt-1.5 h-11" data-testid="p-hoa-address" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Primary contact</Label>
                <Input value={pForm.primary_contact} onChange={(e) => setPForm({ ...pForm, primary_contact: e.target.value })} className="mt-1.5 h-11" data-testid="p-contact" />
              </div>
              <div>
                <Label>Community size (units)</Label>
                <Input type="number" min="0" value={pForm.community_size} onChange={(e) => setPForm({ ...pForm, community_size: e.target.value })} className="mt-1.5 h-11" data-testid="p-size" />
              </div>
            </div>
            <div>
              <Label>Monthly subscription ($)</Label>
              <Input type="number" min="0" step="0.01" value={pForm.monthly_subscription} onChange={(e) => setPForm({ ...pForm, monthly_subscription: e.target.value })} className="mt-1.5 h-11" data-testid="p-subscription" />
              <p className="text-xs text-muted-foreground mt-1">80% of this is directed to advocacy each month.</p>
            </div>
            <div>
              <Label>Mission alignment statement (optional)</Label>
              <Textarea value={pForm.mission_alignment_statement} onChange={(e) => setPForm({ ...pForm, mission_alignment_statement: e.target.value })} className="mt-1.5" placeholder="How your HOA sees this partnership advancing your nonprofit mission…" data-testid="p-mission" />
            </div>
            <DialogFooter>
              <Button type="submit" className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="p-submit">
                <CheckCircle2 className="w-4 h-4 mr-2" /> {registered ? "Save changes" : "Activate partnership"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Donation dialog */}
      <Dialog open={donateOpen} onOpenChange={setDonateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record a tax-deductible donation</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitDonation} className="space-y-4" data-testid="donation-form">
            <div>
              <Label>Donor name</Label>
              <Input required value={dForm.donor_name} onChange={(e) => setDForm({ ...dForm, donor_name: e.target.value })} className="mt-1.5 h-11" data-testid="d-name" />
            </div>
            <div>
              <Label>Donor email (for receipt)</Label>
              <Input type="email" value={dForm.donor_email} onChange={(e) => setDForm({ ...dForm, donor_email: e.target.value })} className="mt-1.5 h-11" data-testid="d-email" />
            </div>
            <div>
              <Label>Amount ($)</Label>
              <Input required type="number" step="0.01" min="1" value={dForm.amount} onChange={(e) => setDForm({ ...dForm, amount: e.target.value })} className="mt-1.5 h-11" data-testid="d-amount" />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Textarea value={dForm.note} onChange={(e) => setDForm({ ...dForm, note: e.target.value })} className="mt-1.5" placeholder="In memory of… / for legal aid programs / etc." data-testid="d-note" />
            </div>
            <div className="text-xs text-muted-foreground bg-accent/40 border border-border rounded-lg p-3">
              Contributions are made to <b className="text-gold">{partnership.nonprofit_name}</b>, a 501(c)(3) public charity (EIN {partnership.nonprofit_ein}). A receipt will be generated immediately.
            </div>
            <DialogFooter>
              <Button type="submit" className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="d-submit">
                <HandHeart className="w-4 h-4 mr-2" /> Record donation
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receipt viewer */}
      <Dialog open={!!receiptDoc} onOpenChange={(o) => !o && setReceiptDoc(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>501(c)(3) Donation Receipt</DialogTitle>
          </DialogHeader>
          {receiptDoc && (
            <div className="royal-bg border border-gold rounded-xl p-6 space-y-4 print:bg-white print:text-black" data-testid="receipt-card">
              <div className="flex items-center justify-between">
                <Crown className="w-6 h-6 text-gold" />
                <div className="text-right">
                  <div className="text-xs text-muted-foreground uppercase">Receipt</div>
                  <div className="font-mono text-gold font-semibold">{receiptDoc.receipt_number}</div>
                </div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Received from</div>
                <div className="text-lg font-semibold">{receiptDoc.donor_name}</div>
                {receiptDoc.donor_email && <div className="text-sm text-muted-foreground">{receiptDoc.donor_email}</div>}
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Amount</div>
                <div className="text-3xl font-mono font-semibold text-gold">{fmt(receiptDoc.amount)}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Donation date: {new Date(receiptDoc.donation_date).toLocaleDateString()} · Issued: {new Date(receiptDoc.issued_on).toLocaleDateString()}
                </div>
              </div>
              {receiptDoc.note && (
                <div>
                  <div className="text-sm text-muted-foreground">Note</div>
                  <div className="italic text-foreground/85">"{receiptDoc.note}"</div>
                </div>
              )}
              <div className="pt-3 border-t border-border text-xs text-muted-foreground leading-relaxed">
                <div className="font-semibold text-gold mb-1">{receiptDoc.nonprofit_name}</div>
                501(c)(3) tax-exempt organization · EIN {receiptDoc.nonprofit_ein}
                <p className="mt-2">{receiptDoc.statement}</p>
              </div>
              <div className="pt-2 print:hidden">
                <Button variant="outline" className="w-full" onClick={() => window.print()} data-testid="print-receipt-btn">
                  <Printer className="w-4 h-4 mr-2" /> Print receipt
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
